import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  getAllActiveBank,
  getAllActiveCountry,
  getAllActiveCountryNationalID,
  getAllActiveReligion,
  getAllActiveMaritalStatus,
  getAllActiveCompany,
  getAllActivePayGrade,
  getAllActiveIncentiveGroup,
  getAllActiveWorkSchedule,
  getAllActiveHolidaySchedule,
  getAllActiveWorkGroup,
  getAllActivePayGroup,
  getAllActivePrefix,
  getAllActiveSSOHospital,
  getAllActiveNationalIDCardType,
  // getAllActiveOTP,
  // getAllActiveStatusTimeLog,
} from "../../services/MasterDataService"
import { getAllSites } from "../../services/SiteService"
import { getAllStatus } from "../../services/StatusService"
import { getAllRoles } from "../../services/RoleService"
import { getAllMenus } from "../../services/MenuService"

// ─── State Type ───────────────────────────────────────────────────────────────

interface MasterDataState {
  banks: any[]
  countries: any[]
  countriesNationalID: any[]
  religions: any[]
  maritalStatuses: any[]
  companies: any[]
  payGrades: any[]
  incentiveGroups: any[]
  workSchedules: any[]
  holidaySchedules: any[]
  workGroups: any[]
  payGroups: any[]
  prefixes: any[]
  ssoHospitals: any[]
  nationalIdCardTypes: any[]
  otps: any[]
  timeLogs: any[]
  sites: any[]
  statuses: any[]
  roles: any[]
  menus: any[]
  loading: boolean
  loaded: boolean // ป้องกัน fetch ซ้ำ
  error: string | null
}

const initialState: MasterDataState = {
  banks: [],
  countries: [],
  countriesNationalID: [],
  religions: [],
  maritalStatuses: [],
  companies: [],
  payGrades: [],
  incentiveGroups: [],
  workSchedules: [],
  holidaySchedules: [],
  workGroups: [],
  payGroups: [],
  prefixes: [],
  ssoHospitals: [],
  nationalIdCardTypes: [],
  otps: [],
  timeLogs: [],
  sites: [],
  statuses: [],
  roles: [],
  menus: [],
  loading: false,
  loaded: false,
  error: null,
}

// ─── Async Thunk ──────────────────────────────────────────────────────────────

export const fetchAllMasterData = createAsyncThunk(
  "masterData/fetchAll",
  async (_, { getState, rejectWithValue }) => {
    const state = (getState() as any).masterData as MasterDataState

    // ถ้าโหลดแล้วไม่ต้อง fetch ซ้ำ
    if (state.loaded) return null

    try {
      const [
        banksRes,
        countriesRes,
        countriesNIDRes,
        religionsRes,
        maritalRes,
        companiesRes,
        payGradesRes,
        incentiveRes,
        workScheduleRes,
        holidayScheduleRes,
        workGroupRes,
        payGroupRes,
        prefixRes,
        ssoHospitalRes,
        nationalIDCardTypeRes,
        // otpRes,
        // timeLogRes,
        sitesRes,
        statusRes,
        rolesRes,
        menusRes,
      ] = await Promise.all([
        getAllActiveBank("All"),
        getAllActiveCountry("All"),
        getAllActiveCountryNationalID("All"),
        getAllActiveReligion("All"),
        getAllActiveMaritalStatus("All"),
        getAllActiveCompany("All"),
        getAllActivePayGrade("All"),
        getAllActiveIncentiveGroup("All"),
        getAllActiveWorkSchedule("All"),
        getAllActiveHolidaySchedule("All"),
        getAllActiveWorkGroup("All"),
        getAllActivePayGroup("All"),
        getAllActivePrefix("All"),
        getAllActiveSSOHospital("All"),
        getAllActiveNationalIDCardType("All"),
        // getAllActiveOTP("All"),
        // getAllActiveStatusTimeLog("All"),
        getAllSites(),
        getAllStatus(),
        getAllRoles(),
        getAllMenus("All"),
      ])

      return {
        banks: banksRes.data?.banks ?? [],
        countries: countriesRes.data?.countries ?? [],
        countriesNationalID: countriesNIDRes.data?.countriesOfNationalId ?? [],
        religions: religionsRes.data?.religions ?? [],
        maritalStatuses: maritalRes.data?.marital_statuses ?? [],
        companies: companiesRes.data?.companies ?? [],
        payGrades: payGradesRes.data?.payGrades ?? [],
        incentiveGroups: incentiveRes.data?.incentiveGroups ?? [],
        workSchedules: workScheduleRes.data?.workSchedules ?? [],
        holidaySchedules: holidayScheduleRes.data?.holidaySchedules ?? [],
        workGroups: workGroupRes.data?.workGroups ?? [],
        payGroups: payGroupRes.data?.payGroups ?? [],
        prefixes: prefixRes.data?.prefixes ?? [],
        ssoHospitals: ssoHospitalRes.data?.hospitals ?? [],
        nationalIdCardTypes:
          nationalIDCardTypeRes.data?.nationalIdCardTypes ?? [],
        // otps: otpRes.data?.otps ?? [],
        // timeLogs: timeLogRes.data?.timeLogs ?? [],
        sites: sitesRes.data?.sites ?? [],
        statuses: statusRes.data?.status ?? [],
        roles: rolesRes.data?.roles ?? [],
        menus: menusRes.data?.menus ?? [],
      }
    } catch (error) {
      return rejectWithValue("Failed to fetch master data")
    }
  }
)

// ─── Slice ────────────────────────────────────────────────────────────────────

const masterDataSlice = createSlice({
  name: "masterData",
  initialState,
  reducers: {
    // ใช้ reset เมื่อต้องการ force refetch (เช่น หลัง create/update master)
    resetMasterData: (state) => {
      state.loaded = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllMasterData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllMasterData.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload === null) return // loaded แล้ว ไม่ต้องทำอะไร
        const d = action.payload
        state.banks = d.banks
        state.countries = d.countries
        state.countriesNationalID = d.countriesNationalID
        state.religions = d.religions
        state.maritalStatuses = d.maritalStatuses
        state.companies = d.companies
        state.payGrades = d.payGrades
        state.incentiveGroups = d.incentiveGroups
        state.workSchedules = d.workSchedules
        state.holidaySchedules = d.holidaySchedules
        state.workGroups = d.workGroups
        state.payGroups = d.payGroups
        state.prefixes = d.prefixes
        state.ssoHospitals = d.ssoHospitals
        state.nationalIdCardTypes = d.nationalIdCardTypes
        // state.otps = d.otps
        // state.timeLogs = d.timeLogs
        state.sites = d.sites
        state.statuses = d.statuses
        state.roles = d.roles
        state.menus = d.menus
        state.loaded = true
      })
      .addCase(fetchAllMasterData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { resetMasterData } = masterDataSlice.actions

// ─── Refresh: reset loaded flag then re-fetch ─────────────────────────────────
export const refreshMasterData = () => (dispatch: any) => {
  dispatch(resetMasterData())
  dispatch(fetchAllMasterData())
}

export default masterDataSlice
